import {useCallback, useEffect, useRef, useState} from 'react';
import {customerConfig, customerFeatureEnabled} from '../config/customer';
import {createImpersonation} from '../impersonation-api';

export const useViewAppAsUser = (userId) => {
    const [starting, setStarting] = useState(false);
    const [error, setError] = useState('');
    const [handoff, setHandoff] = useState(null);
    const busy = useRef(false);
    const active = useRef(true);
    const tab = useRef(null);
    const timer = useRef(null);
    const settings = customerConfig();
    const enabled = customerFeatureEnabled();

    useEffect(() => {
        active.current = true;

        return () => {
            active.current = false;
            clearTimeout(timer.current);
            tab.current?.close();
        };
    }, []);

    useEffect(() => {
        setError('');
        setHandoff(null);
        clearTimeout(timer.current);
    }, [userId]);

    const start = useCallback(async (event) => {
        event?.stopPropagation?.();

        if (busy.current || !enabled || !settings || !userId || handoff) {
            return;
        }

        busy.current = true;
        setStarting(true);
        setError('');

        try {
            tab.current = window.open('about:blank', '_blank');
        } catch {
            tab.current = null;
        }

        const deadline = Date.now() + 60000;
        const result = await createImpersonation(userId, settings.apiBaseUrl);

        if (!active.current) {
            return;
        }

        const fail = (text) => {
            tab.current?.close();
            tab.current = null;
            setError(text);
        };

        if (result.error) {
            fail(result.error);
        } else {
            const parsedExpiry = Date.parse(result.expiresAt);
            const expiresAt = Number.isFinite(parsedExpiry) ? Math.min(deadline, parsedExpiry) : deadline;
            const url = `https://v2.fitlap.ee/impersonation#code=${result.code}`;

            if (expiresAt <= Date.now()) {
                fail('The handoff expired. Start a new user view.');
            } else {
                let navigated = false;

                try {
                    if (tab.current && !tab.current.closed) {
                        tab.current.opener = null;
                        tab.current.location.replace(url);
                        navigated = true;
                    }
                } catch {
                    // Fall back to a deliberate link; never report the URL in errors.
                }

                if (!navigated) {
                    tab.current?.close();
                    setHandoff({url, expiresAt});
                    timer.current = setTimeout(() => {
                        setHandoff(null);
                        setError('The handoff expired. Start a new user view.');
                    }, expiresAt - Date.now());
                }

                tab.current = null;
            }
        }

        busy.current = false;
        setStarting(false);
    }, [enabled, handoff, settings, userId]);

    const openHandoff = useCallback((event) => {
        event?.stopPropagation?.();

        if (!handoff) {
            return;
        }

        if (Date.now() >= handoff.expiresAt) {
            setError('The handoff expired. Start a new user view.');
            setHandoff(null);
            clearTimeout(timer.current);
            return;
        }

        clearTimeout(timer.current);
        setHandoff(null);
    }, [handoff]);

    return {
        enabled,
        settings,
        starting,
        error,
        handoff,
        start,
        openHandoff,
    };
};
